import React, { FunctionComponent, useContext } from 'react';
import { Box, Heading } from '@wix/design-system';
import { useRouter } from 'next/router';
import { useTranslation } from '@wix/wix-i18n-config';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { BI, EXPERIMENTS, LOCALES, PAGES } from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { InnerLink } from '../InnerLink';
import { SearchBar } from '../SearchBar/Index';
import { useBI } from '../../hooks/useBI';
import { mapPageTypeToSourceName } from '../../utils/bi';
import { PageType } from '../../types';
import css from './SubHeader.module.scss';
import { Article } from '@wix/answers-api';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { COLORS } from '../../utils/theme';
import { RoadmapHeader } from '../RoadmapHeader';
import { Context } from '../../context';
import { useHistoryContext } from '../../hooks/useRouteHistory';

type SubHeaderProps = {
  pageType: PageType;
  itemId?: string;
  itemType?: string;
  article?: Article;
  isRoadmapArticlePage?: boolean;
  roadmapLabels?: {
    id: string;
    name: string;
  }[];
};

export const SubHeader: FunctionComponent<SubHeaderProps> = ({
  pageType,
  itemId,
  itemType,
  isRoadmapArticlePage,
  roadmapLabels,
}: SubHeaderProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const { prePageUrl } = useHistoryContext();

  const { experiments } = useExperiments({ readOnly: true });

  const isNewHomepage =
    experiments.enabled(EXPERIMENTS.SPECS.NEW_HOMEPAGE) ||
    experiments.enabled(EXPERIMENTS.SPECS.ENABLE_WPR_AND_NEW_HOME_PAGE);

  const isWixRoadmap =
    experiments.enabled(EXPERIMENTS.SPECS.WIX_ROADMAP) ||
    experiments.enabled(EXPERIMENTS.SPECS.ENABLE_WPR_AND_NEW_HOME_PAGE);

  const { sendBIEvent } = useBI();
  const { isMobile, isTablet } = useContext(Context);

  const hideSubHeaderForMobileRoadmap =
    isWixRoadmap &&
    (pageType === PAGES.ROADMAP ||
      (pageType === PAGES.ARTICLE && isRoadmapArticlePage)) &&
    (isMobile || isTablet);

  const isPrePageIsRoadmap =
    prePageUrl?.includes('/roadmap') && pageType === PageType.Article;

  const onHomepageClick = async (): Promise<void> => {
    const sourceName = mapPageTypeToSourceName(pageType);
    await sendBIEvent(
      pageClick({
        source_name: sourceName,
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.HOME_PAGE,
        clicked_text: t('subheader.wix.center'),
        clicked_url: `${location.origin}/${locale}`,
        is_page_content_click:
          sourceName === BI.SOURCE_NAMES.ARTICLE ? false : undefined,
      })
    );
  };

  const showRoadMapBanner =
    (pageType === PAGES.ROADMAP || isRoadmapArticlePage) &&
    isWixRoadmap &&
    locale === LOCALES.EN;
  return (
    <Box
      direction="vertical"
      verticalAlign="middle"
      backgroundColor={COLORS.D10}
      className={`${css.wrapper} ${isNewHomepage ? css.newHomePage : ''} ${
        isRoadmapArticlePage || pageType === PAGES.ROADMAP || isPrePageIsRoadmap
          ? css.roadmapPage
          : ''
      }`}
    >
      {!hideSubHeaderForMobileRoadmap && (
        <Box
          className={css.subHeaderWrapper}
          verticalAlign="middle"
          align="space-between"
        >
          <Heading
            size="medium"
            light
            className={css.backToHomepageLink}
            as="div"
          >
            <InnerLink
              href="/"
              className={css.backToHomepageLink}
              dataHook={DATA_HOOKS.SUBHEADER_GO_TO_HOMEPAGE}
              onClick={onHomepageClick}
            >
              {t('subheader.wix.center')}
            </InnerLink>
          </Heading>
          <SearchBar itemId={itemId} itemType={itemType} pageType={pageType} />
        </Box>
      )}
      {showRoadMapBanner ? (
        <RoadmapHeader
          isArticlePage={pageType === PageType.Article}
          roadmapLabels={roadmapLabels}
          selectedLabelId={itemId}
        />
      ) : null}
    </Box>
  );
};
